import { getFunctions, httpsCallable } from 'firebase/functions';

/** Public Auth0 SPA client ID from Sequence's web app (not secret). */
export const SEQUENCE_AUTH0_CLIENT_ID = 'oYIhXhAJH9MCejmnoCjYyUhuYckEjiGV';

export type SequenceCallOptions = {
  accessToken?: string;
  graphqlAccessToken?: string;
  refreshToken?: string;
  auth0ClientId?: string;
};

function sequenceCallPayload(options?: SequenceCallOptions): Record<string, string> {
  if (!options) return {};
  const payload: Record<string, string> = {};
  if (options.accessToken?.trim()) payload.accessToken = options.accessToken.trim();
  if (options.graphqlAccessToken?.trim()) payload.graphqlAccessToken = options.graphqlAccessToken.trim();
  if (options.refreshToken?.trim()) payload.refreshToken = options.refreshToken.trim();
  if (options.auth0ClientId?.trim()) payload.auth0ClientId = options.auth0ClientId.trim();
  return payload;
}

export async function testSequenceConnection(options?: SequenceCallOptions): Promise<{
  ok: boolean;
  authMode?: string;
  autoRenewEnabled?: boolean;
  graphqlConfigured?: boolean;
  identifiedCount?: number;
  needsReviewCount?: number;
  organizationId: string;
  organizationName: string;
  podCount: number;
  cardCount: number;
  restAccountCount?: number;
  restValidated?: boolean;
  omniSupported: boolean;
  sampleCards: Array<{ id: string; type: string; lastFour: string; holderName: string }>;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'testSequenceConnection');
  const result = await fn(sequenceCallPayload(options));
  return result.data as {
    ok: boolean;
    authMode?: string;
    autoRenewEnabled?: boolean;
    organizationId: string;
    organizationName: string;
    podCount: number;
    cardCount: number;
    restAccountCount?: number;
    restValidated?: boolean;
    omniSupported: boolean;
    sampleCards: Array<{ id: string; type: string; lastFour: string; holderName: string }>;
  };
}

export async function syncSequenceCards(options?: SequenceCallOptions): Promise<{
  imported: number;
  identifiedCount?: number;
  needsReviewCount?: number;
  registryTotal: number;
  linkedCount: number;
  organizationId: string;
  transactionsWritten?: number;
  matched?: number;
  assigned?: number;
  cardExpensesWritten?: number;
  cardExpensesUpdated?: number;
  shortTermOpsWritten?: number;
  shortTermOpsStamped?: number;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'syncSequenceCards');
  const result = await fn(sequenceCallPayload(options));
  return result.data as {
    imported: number;
    identifiedCount?: number;
    needsReviewCount?: number;
    registryTotal: number;
    linkedCount: number;
    organizationId: string;
    transactionsWritten?: number;
    matched?: number;
    assigned?: number;
    cardExpensesWritten?: number;
    cardExpensesUpdated?: number;
    shortTermOpsWritten?: number;
    shortTermOpsStamped?: number;
  };
}

export async function syncSequenceTransactions(options?: SequenceCallOptions): Promise<{
  written: number;
  matched: number;
  assigned: number;
  shortTermOpsWritten?: number;
  shortTermOpsStamped?: number;
  cardExpensesWritten?: number;
  cardExpensesUpdated?: number;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'syncSequenceTransactions');
  const result = await fn(sequenceCallPayload(options));
  return result.data as {
    written: number;
    matched: number;
    assigned: number;
    shortTermOpsWritten?: number;
    shortTermOpsStamped?: number;
    cardExpensesWritten?: number;
    cardExpensesUpdated?: number;
  };
}

/**
 * Record a fresh Sequence pod balance snapshot in the daily finance report
 * (`financeSettings/dailyFinanceReport`). Finance leadership (admin, COO, CFO,
 * EA) after the matching functions deploy; older functions stay admin-only.
 */
export async function syncDailyFinanceReport(): Promise<{
  dateKey: string;
  sequencePodAmount: number;
  podCount: number;
}> {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'syncDailyFinanceReport');
  const result = await fn({});
  return result.data as { dateKey: string; sequencePodAmount: number; podCount: number };
}
